/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { DATE_FORMATS } from 'constants/DateFormats'
import { LeadStatus } from 'types/enums/LeadStatus'

dayjs.extend(customParseFormat)

export const isClosingDateEditableStatus = (status?: LeadStatus | null): boolean =>
    status === LeadStatus.Acceptance || status === LeadStatus.Rejection

export const parseLeadDateField = (value?: string | null): Date | undefined => {
    if (!value?.trim()) {
        return undefined
    }

    const trimmed = value.trim()
    const dmy = dayjs(trimmed, DATE_FORMATS.DATE_DMY, true)
    if (dmy.isValid()) {
        return dmy.toDate()
    }

    const ymd = dayjs(trimmed, DATE_FORMATS.DATE_YMD, true)
    if (ymd.isValid()) {
        return ymd.toDate()
    }

    const parsed = dayjs(trimmed)
    return parsed.isValid() ? parsed.toDate() : undefined
}

/** Backend expects opportunity date fields as DD.MM.YYYY. */
export const formatLeadDateField = (value?: Date | null): string | null => {
    if (!value) {
        return null
    }

    return dayjs(value).format(DATE_FORMATS.DATE_DMY)
}

/** Default Erstellungsdatum for new opportunities. */
export const getDefaultOpportunityCreatedDate = (): Date => dayjs().startOf('day').toDate()

/** Keeps submitted opportunity date fields when the API omits them after save. */
export const mergeLeadAfterSave = <T extends { created_date?: string | null; closed_at?: string | null }>(
    submitted: Partial<T>,
    response: T
): T => {
    const merged = {...response}

    if (
        (merged.created_date === undefined || merged.created_date === null || merged.created_date === '') &&
        submitted.created_date
    ) {
        merged.created_date = submitted.created_date
    }

    if (
        (merged.closed_at === undefined || merged.closed_at === null || merged.closed_at === '') &&
        submitted.closed_at
    ) {
        merged.closed_at = submitted.closed_at
    }

    return merged
}
